/**
 * Pure helpers for tournament-related route gating.
 *
 * Extracted from pages/Game.tsx so the redirect predicate can be unit-tested
 * without standing up the full Game page (Redux store, router, contexts, etc).
 */

type PlayerTournamentDetail = {
  tournamentId: number | string;
  status?: string;
  gameID?: number | string;
  shouldAutoRedirect?: boolean;
};

type TablesById = Record<
  string,
  {
    game?: { tournamentId?: number | string; gameID?: number | string; isPostBalancing?: number };
    currentPlayer?: { isSeated?: boolean };
  }
>;

/**
 * Decide whether an active tournament player ('P' status) needs to be sent
 * back to their assigned table. Returns the target gameID to redirect to,
 * or null if no redirect is required.
 *
 * Returns null when:
 *  - The current table isn't part of any tournament (cash table).
 *  - The player has no record for this tournament (not registered).
 *  - The player's status isn't 'P' (busted 'F' or pre-game 'R' can observe freely).
 *  - The player has no seat yet (gameID -1 / missing).
 *  - The player is already at their assigned table.
 *  - `tablesById` is supplied AND the assigned table is hydrated but shows the
 *    player NOT seated there (see the stale-'P' guard below).
 */
export function computeActiveTournamentRedirect(
  tournamentId: number | string | undefined | null,
  currentGameID: number | string | undefined | null,
  playerTournamentDetails: PlayerTournamentDetail[] | undefined | null,
  tablesById?: TablesById | null
): number | null {
  // Falsy tournamentId (undefined / null / '' / 0) means "not a tournament
  // table" — cash tables sometimes ship 0 instead of omitting the field.
  if (!tournamentId) return null;
  if (currentGameID === undefined || currentGameID === null || currentGameID === '') return null;
  const list = playerTournamentDetails || [];
  const detail = list.find((d) => Number(d.tournamentId) === Number(tournamentId));
  if (!detail || detail.status !== 'P') return null;
  if (detail.gameID === null || detail.gameID === undefined) return null;
  const assigned = Number(detail.gameID);
  if (!Number.isFinite(assigned) || assigned === -1) return null;
  if (assigned === Number(currentGameID)) return null;
  // Stale-'P' guard. `playerTournamentDetails` comes from the lobby feed
  // (gotTnmStatus); when that feed lags — e.g. the player busted out but the
  // record still reads status 'P' with their OLD gameID — this predicate would
  // otherwise yank them onto that dead table, where they land on a stale
  // "joining" seat that never resolves. If we have the assigned table hydrated
  // in state.tables and it POSITIVELY shows the player is not seated there,
  // trust that fresher signal over the stale record and suppress the redirect.
  // When the assigned table isn't hydrated we can't tell, so we still allow the
  // redirect (its whole point is to go hydrate the assigned seat).
  if (tablesById) {
    const assignedState = tablesById[String(assigned)];
    if (assignedState && assignedState.currentPlayer?.isSeated === false) return null;
  }
  return assigned;
}

/**
 * Fallback redirect resolver that doesn't depend on lobby data
 * (`playerTournamentDetails` is only populated while Lobby.tsx is mounted —
 * reloading directly into /table/X bypasses it). Scans `state.tables` for
 * any OTHER table with the same `tournamentId` where the player is seated,
 * and returns its gameID. If found, the current table is a "wrong table"
 * the player has wandered into within their active tournament.
 *
 * Returns null when no such seated sibling exists — covers cash tables
 * (no tournamentId), tournaments the player isn't seated in (observing
 * many tables is fine), and busted-out players (isSeated will be false
 * everywhere).
 */
export function computeActiveTournamentRedirectFromSeat(
  tournamentId: number | string | undefined | null,
  currentGameID: number | string | undefined | null,
  tablesById: TablesById | undefined | null
): number | null {
  // Falsy tournamentId (undefined / null / '' / 0) means "not a tournament
  // table" — cash tables sometimes ship 0 instead of omitting the field.
  if (!tournamentId) return null;
  if (currentGameID === undefined || currentGameID === null || currentGameID === '') return null;
  const tables = tablesById || {};
  const currentGidStr = String(currentGameID);
  const targetTournamentNum = Number(tournamentId);
  for (const [otherGid, otherTable] of Object.entries(tables)) {
    if (otherGid === currentGidStr) continue;
    const otherTournament = otherTable?.game?.tournamentId;
    if (otherTournament === undefined || otherTournament === null) continue;
    if (Number(otherTournament) !== targetTournamentNum) continue;
    if (otherTable?.currentPlayer?.isSeated !== true) continue;
    const otherGameID = otherTable?.game?.gameID;
    if (otherGameID === undefined || otherGameID === null) continue;
    const assigned = Number(otherGameID);
    if (!Number.isFinite(assigned) || assigned === -1) continue;
    if (assigned === Number(currentGameID)) continue;
    return assigned;
  }
  return null;
}

/**
 * Last-resort redirect resolver for when the table being viewed is DARK — its
 * felt never hydrated (no gotTexas), so `state.tables[gid].game.tournamentId` is
 * undefined and BOTH resolvers above return null (they key off the current
 * table's tournamentId). This is the closed-table / final-rebalancing case: the
 * player's old table closed, the engine moved them to a new seat, but the client
 * is still parked on the dead table's URL with nothing to identify it by.
 *
 * Uses the player's OWN tournament record instead of the current table's
 * identity: if the server has raised `shouldAutoRedirect` (registered +
 * tournament in progress + seat assigned + not maintenance/break/closed) on a
 * 'P' record whose assigned gameID is NOT the table being viewed, send them to
 * that seat. `shouldAutoRedirect` is the same server-computed "this player
 * belongs at their seat right now" flag TournamentDetailModal redirects on, so it
 * can't fire for an observer of a cash table (the flag is false unless they're an
 * active seated tournament player).
 *
 * Guarded against the un-hydrated-own-table false positive: if ANY active 'P'
 * record's assigned seat IS the current table, the player is already where they
 * belong (the felt is merely still loading) and we never redirect — this stops a
 * second concurrent tournament's record from yanking them off their own seat
 * before gotTexas arrives.
 *
 * Exported for unit testing.
 */
export function computeActiveTournamentRedirectFromAssignment(
  currentGameID: number | string | undefined | null,
  playerTournamentDetails: PlayerTournamentDetail[] | undefined | null
): number | null {
  if (currentGameID === undefined || currentGameID === null || currentGameID === '') return null;
  const list = playerTournamentDetails || [];
  // Already at (or assigned to) the current table on some active record → the
  // felt is just loading; do not redirect.
  const atAssignedSeat = list.some(
    (d) => d?.status === 'P' && Number(d?.gameID) === Number(currentGameID)
  );
  if (atAssignedSeat) return null;
  const detail = list.find((d) => d?.status === 'P' && d?.shouldAutoRedirect === true);
  if (!detail || detail.gameID === null || detail.gameID === undefined) return null;
  const assigned = Number(detail.gameID);
  if (!Number.isFinite(assigned) || assigned === -1) return null;
  if (assigned === Number(currentGameID)) return null;
  return assigned;
}

/**
 * When a player becomes an ACTIVE seated tournament player (status 'P' with a
 * real assigned seat), any OTHER tables they still have open for that SAME
 * tournament are stale and should be closed — the old seat after a rebalance, or
 * tables they were observing while busted. Left open they linger in the table
 * switcher / "Your Tables" and eat into the open-table limit (reported: hitting
 * "Reached Table Limit" after Re-Entry because the observed tables stayed open).
 *
 * Returns the gameIDs (state.tables keys) of those stale tables to close. The
 * player's CURRENT seat (the assigned gameID) is always KEPT. Strictly
 * per-tournament: only tables of a tournament the player is actively playing are
 * considered, so a seat in one tournament never closes observed tables of a
 * DIFFERENT tournament (or a cash table). View-agnostic — the caller removes the
 * returned ids from state.tables, which covers both the single-table switcher
 * and grid cells.
 *
 * Exported for unit testing.
 */
export function computeObservedTablesToClose(
  playerTournamentDetails: PlayerTournamentDetail[] | undefined | null,
  tablesById: TablesById | undefined | null
): string[] {
  const toClose = new Set<string>();
  const list = Array.isArray(playerTournamentDetails) ? playerTournamentDetails : [];
  const tables = tablesById || {};
  for (const d of list) {
    // Only reconcile when the player is actively PLAYING with a real seat.
    if (d?.status !== 'P') continue;
    if (d.gameID === null || d.gameID === undefined) continue;
    const assigned = Number(d.gameID);
    if (!Number.isFinite(assigned) || assigned === -1) continue;
    const tid = Number(d.tournamentId);
    if (!Number.isFinite(tid)) continue;
    for (const [gid, table] of Object.entries(tables)) {
      if (Number(table?.game?.tournamentId) !== tid) continue; // same tournament only
      const tableGameId = Number(table?.game?.gameID ?? gid);
      if (tableGameId === assigned) continue; // keep the current seat table
      toClose.add(String(gid));
    }
  }
  return Array.from(toClose);
}

/**
 * Whether a sweep that closes a player's stale same-tournament tables must
 * leave `gameID` alone for now because a rebalance is still moving them off
 * it. Shared by App.tsx's two sweeps — the details-feed reconcile and the
 * got_playerGames wrong-tournament cleanup — so neither can retire the
 * balancing SOURCE while TournamentScreenModal still owns its teardown.
 *
 * Held when:
 *  - the table is mid final-table reshuffle (isPostBalancing = 1);
 *  - it was the player's own recent seat, or is a protected balancing table;
 *  - the player's newly assigned seat in its tournament hasn't loaded yet:
 *    removing the source then leaves the tournament with no table at all,
 *    which a two-table grid reads as one table and hands off to single view
 *    of the OTHER table (EN-10625).
 * The engine closing the table overrides all but the reshuffle: a dead table
 * is dead whatever the client's balancing bookkeeping says.
 *
 * Exported for unit testing.
 */
export function shouldHoldTableThroughRebalance(
  gameID: string,
  playerTournamentDetails: PlayerTournamentDetail[] | undefined | null,
  tablesById: TablesById | undefined | null,
  signals: { engineClosed: boolean; wasMySeat: boolean; isProtected: boolean }
): boolean {
  const table = tablesById?.[gameID];
  if (table?.game?.isPostBalancing === 1) return true;
  if (signals.engineClosed) return false;
  if (signals.wasMySeat || signals.isProtected) return true;
  const tid = Number(table?.game?.tournamentId);
  if (!Number.isFinite(tid)) return false;
  const list = Array.isArray(playerTournamentDetails) ? playerTournamentDetails : [];
  const assigned = list.find(
    (d) =>
      d?.status === 'P' &&
      Number(d?.tournamentId) === tid &&
      d?.gameID !== null &&
      d?.gameID !== undefined
  );
  if (!assigned) return false;
  const assignedGame = tablesById?.[String(assigned.gameID)]?.game;
  const assignedHydrated = Boolean(assignedGame?.gameID) && Number(assignedGame?.gameID) !== 0;
  return !assignedHydrated;
}

/**
 * Guests must not observe tournament/SnG tables — cash observation stays
 * allowed. `tabletype` only resolves once the table's subscribe response
 * (GOT_TEXAS/GOT_OMAHA) arrives, so this returns false until then; a guest
 * can briefly land on the felt before the caller's effect re-runs and acts
 * on a `true` result.
 */
export function shouldBlockGuestFromTournamentTable(
  guest: boolean,
  tableType: string | undefined | null
): boolean {
  return guest && (tableType === 'm' || tableType === 't');
}
